/**
 * タイムコード共通ユーティリティ
 *
 * コメント本文中の再生位置タグ `[M:SS]` / `[M:SS.FF]` / `[N]` を扱う。
 * FF はフレーム番号（0 始まり・fps 未満）で、レガシー `pin_time` の
 * "M:SS.FF" 形式と互換。`[N]`（コロンなしの数字のみ）はメディア先頭からの
 * 絶対フレーム番号で、対象メディアの fps で秒に換算する（例: [300] @30fps = 10 秒）。
 * 絶対フレームのタグ括弧なし文字列表現は "300F" 形式（BE 側と共通仕様）。
 * fps が取得できないメディアは DEFAULT_FPS で換算する。
 */

/** fps 不明時のデフォルト値（オーディオファイルもこの値で換算する） */
export const DEFAULT_FPS = 30;

/**
 * 本文中の [M:SS] / [M:SS.FF] タグ。
 * 直後に '(' が続く場合は Markdown リンク構文 [M:SS](url) としてスキップ。
 */
export const TIMESTAMP_TAG_RE = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,2}))?\](?!\()/;

/**
 * 本文中の [N]（絶対フレーム番号）タグ。
 * 直後に '(' が続く場合は Markdown リンク構文 [N](url) としてスキップ。
 */
export const FRAME_TAG_RE = /\[(\d+)\](?!\()/;

/**
 * [M:SS(.FF)] と [N] の複合タグ（本文スキャン用）。
 * グループ 1-3 = タイムコード（分・秒・フレーム）、グループ 4 = 絶対フレーム番号。
 */
export const BRACKET_TAG_RE = new RegExp(
  `${TIMESTAMP_TAG_RE.source}|${FRAME_TAG_RE.source}`,
);

/** タグ括弧なしのタイムコード文字列（"M:SS" / "M:SS.FF"）の厳密パターン */
const TIMECODE_RE = /^(\d{1,2}):(\d{2})(?:\.(\d{1,2}))?$/;

/** タグ括弧なしの絶対フレーム文字列（"300F"）の厳密パターン */
const FRAME_STRING_RE = /^(\d+)F$/;

/** コードブロック・インラインコードでテキストを分割する正規表現（キャプチャで区切り文字も保持） */
export const CODE_BLOCK_SPLIT_RE = /(```[\s\S]*?```|`[^`]+`)/g;

/** タイムコードの構成要素 */
export interface TimecodeParts {
  minutes: number;
  seconds: number;
  /** フレーム番号（タイムコードに .FF が無い場合は null） */
  frames: number | null;
}

/**
 * "M:SS" / "M:SS.FF" / "300F"（絶対フレーム）文字列を構成要素に分解。不正値は null。
 * 絶対フレームは minutes/seconds = 0 の frames のみとして表現する
 * （timecodePartsToSeconds が frames / fps でそのまま秒に換算できる）。
 */
export function parseTimecodeParts(timecode: string): TimecodeParts | null {
  if (!timecode) return null;
  const frameMatch = timecode.match(FRAME_STRING_RE);
  if (frameMatch) {
    return { minutes: 0, seconds: 0, frames: parseInt(frameMatch[1], 10) };
  }
  const match = timecode.match(TIMECODE_RE);
  if (!match) return null;
  return {
    minutes: parseInt(match[1], 10),
    seconds: parseInt(match[2], 10),
    frames: match[3] !== undefined ? parseInt(match[3], 10) : null,
  };
}

/** TimecodeParts を秒数（小数）に変換 */
export function timecodePartsToSeconds(parts: TimecodeParts, fps: number = DEFAULT_FPS): number {
  const safeFps = fps > 0 ? fps : DEFAULT_FPS;
  const frameSeconds = parts.frames !== null ? parts.frames / safeFps : 0;
  return parts.minutes * 60 + parts.seconds + frameSeconds;
}

/**
 * timecode 文字列（"M:SS" / "M:SS.FF"）を秒数に変換。空文字や不正値は null を返す。
 * .FF 付きの場合は fps でフレーム→秒に換算する。
 */
export function parseTimecode(timecode: string, fps: number = DEFAULT_FPS): number | null {
  const parts = parseTimecodeParts(timecode);
  if (!parts) return null;
  return timecodePartsToSeconds(parts, fps);
}

/** 秒数を最も近いフレーム境界に量子化する */
export function quantizeToFrame(seconds: number, fps: number = DEFAULT_FPS): number {
  const safeFps = fps > 0 ? fps : DEFAULT_FPS;
  return Math.round(seconds * safeFps) / safeFps;
}

/**
 * 秒数をタイムコード文字列に変換する。
 * - withFrames: false → "M:SS"（従来互換・切り捨て）
 * - withFrames: true  → "M:SS.FF"（最も近いフレームに量子化。29.97fps 等の非整数 fps も安全）
 */
export function formatTimecode(
  totalSeconds: number,
  options: { fps?: number; withFrames?: boolean } = {},
): string {
  const { fps = DEFAULT_FPS, withFrames = false } = options;
  const safeSeconds = Math.max(0, Number.isFinite(totalSeconds) ? totalSeconds : 0);

  if (!withFrames) {
    const minutes = Math.floor(safeSeconds / 60);
    const seconds = Math.floor(safeSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  const safeFps = fps > 0 ? fps : DEFAULT_FPS;
  // フレーム総数に量子化してから分解する（浮動小数の繰り上がり事故を防ぐ）
  const totalFrames = Math.round(safeSeconds * safeFps);
  let wholeSeconds = Math.floor(totalFrames / safeFps);
  let frames = Math.round(totalFrames - wholeSeconds * safeFps);
  // 非整数 fps の丸めで frames が 1 秒分に達した場合は繰り上げる
  if (frames >= Math.round(safeFps)) {
    wholeSeconds += 1;
    frames = 0;
  }
  const minutes = Math.floor(wholeSeconds / 60);
  const seconds = wholeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}.${frames.toString().padStart(2, '0')}`;
}

/** formatTimecode の [ ] 括弧付き版（コメント本文へ挿入するタグ形式） */
export function formatBracketTimecode(
  totalSeconds: number,
  options: { fps?: number; withFrames?: boolean } = {},
): string {
  return `[${formatTimecode(totalSeconds, options)}]`;
}

/** テキストをコード領域と非コード領域に分割し、非コード領域のみにコールバックを適用 */
export function mapNonCodeSegments(text: string, fn: (_segment: string) => string): string {
  return text.split(CODE_BLOCK_SPLIT_RE).map((seg, i) => (i % 2 === 1 ? seg : fn(seg))).join('');
}

/**
 * [M:SS] / [M:SS.FF] / [N] パターンを検出し、クリック可能なタグに変換する前処理（表示時は括弧を除去）。
 * コードブロック・インラインコード内のパターンはスキップし、
 * Markdown リンク構文 [M:SS](url) も変換しない。
 * href にはタイムコード文字列をそのまま埋め込む（例: #ts-1:23.15、絶対フレームは #ts-f300）。
 * fps はクリック時に対象メディアから解決するため、ここでは秒換算しない。
 */
export function preprocessTimestamps(text: string): string {
  return mapNonCodeSegments(text, segment =>
    segment.replace(
      new RegExp(BRACKET_TAG_RE.source, 'g'),
      (_match, mm, ss, ff, frames) => {
        if (frames !== undefined) {
          return `<a class="timestamp-link" href="#ts-f${frames}">${frames}</a>`;
        }
        const label = ff !== undefined ? `${mm}:${ss}.${ff}` : `${mm}:${ss}`;
        return `<a class="timestamp-link" href="#ts-${label}">${label}</a>`;
      },
    ),
  );
}

/**
 * `#ts-` 形式の href 値からタイムコード構成要素を取り出す。
 * 絶対フレーム形式 `#ts-f<フレーム番号>` と旧形式 `#ts-<整数秒>` にも対応する。
 */
export function parseTimestampHref(href: string): TimecodeParts | null {
  if (!href.startsWith('#ts-')) return null;
  const raw = href.slice(4);
  // 絶対フレーム: #ts-f300 → 0 秒 + 300 フレーム（受け手が fps で換算）
  const frameMatch = raw.match(/^f(\d+)$/);
  if (frameMatch) {
    return { minutes: 0, seconds: 0, frames: parseInt(frameMatch[1], 10) };
  }
  const parts = parseTimecodeParts(raw);
  if (parts) return parts;
  // 旧形式: 整数秒
  if (/^\d+$/.test(raw)) {
    const total = parseInt(raw, 10);
    return { minutes: Math.floor(total / 60), seconds: total % 60, frames: null };
  }
  return null;
}

/**
 * コメント本文から最初の [M:SS] / [M:SS.FF] / [N] パターンを抽出し、
 * 括弧なしのタイムコード文字列で返す（絶対フレームは "300F" 形式）。
 * コードブロック・インラインコード・Markdown リンクはスキップ。見つからなければ "0:00"。
 */
export function extractFirstTimecode(text: string): string {
  const segments = text.split(CODE_BLOCK_SPLIT_RE);
  for (let i = 0; i < segments.length; i++) {
    if (i % 2 === 1) continue;
    const match = segments[i].match(BRACKET_TAG_RE);
    if (match) {
      if (match[4] !== undefined) return `${match[4]}F`;
      return match[3] !== undefined
        ? `${match[1]}:${match[2]}.${match[3]}`
        : `${match[1]}:${match[2]}`;
    }
  }
  return '0:00';
}

/** 本文にタイムコードタグ（[M:SS] / [M:SS.FF] / [N]）が含まれるか（コード領域は除外） */
export function hasTimecode(text: string): boolean {
  const segments = text.split(CODE_BLOCK_SPLIT_RE);
  for (let i = 0; i < segments.length; i++) {
    if (i % 2 === 1) continue;
    if (BRACKET_TAG_RE.test(segments[i])) return true;
  }
  return false;
}

/** splitTimecodeSegments の分解結果。 */
export type TimecodeSegment =
  | { type: 'text'; value: string }
  | { type: 'timecode'; value: string };

/**
 * 本文をテキストとタイムコードタグに分解する。
 * タグ（[M:SS] / [M:SS.FF] / [N]）は括弧なしのタイムコード文字列
 * （絶対フレームは "300F"）として返し、テキスト側からは取り除かれる。
 * 「タグのテキスト表示を UI（チップ等）に置き換える」表示に使う。
 * コードブロック・インラインコード・Markdown リンクはテキストのまま返す。
 */
export function splitTimecodeSegments(text: string): TimecodeSegment[] {
  const out: TimecodeSegment[] = [];
  const pushText = (value: string) => {
    if (!value) return;
    const last = out[out.length - 1];
    if (last && last.type === 'text') {
      last.value += value;
    } else {
      out.push({ type: 'text', value });
    }
  };

  const segments = text.split(CODE_BLOCK_SPLIT_RE);
  segments.forEach((segment, i) => {
    if (i % 2 === 1) {
      pushText(segment);
      return;
    }
    const re = new RegExp(BRACKET_TAG_RE.source, 'g');
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(segment)) !== null) {
      pushText(segment.slice(lastIndex, match.index));
      const [, mm, ss, ff, frames] = match;
      const value =
        frames !== undefined
          ? `${frames}F`
          : ff !== undefined
            ? `${mm}:${ss}.${ff}`
            : `${mm}:${ss}`;
      out.push({ type: 'timecode', value });
      lastIndex = match.index + match[0].length;
    }
    pushText(segment.slice(lastIndex));
  });
  return out;
}
